/**
 * SmswiserCampaignPicker
 * Professional campaign selector with search – single or multi-select
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, useCallback, useMemo, useState } from 'react';
import { ChevronDown, Layers, Pin, PinOff, Search } from 'lucide-react';
import * as Popover from '@radix-ui/react-popover';
import * as Tooltip from '@radix-ui/react-tooltip';

import { Button } from '@/components/ui';
import { cn } from '@/lib/utils';

export interface CampaignOption {
  id: number;
  name: string;
  /** Full label for tooltip when truncated (e.g. full campaign list) */
  fullLabel?: string;
  /** All wiserIds in this group (for pinning the whole group) */
  wiserIds?: number[];
}

interface SmswiserCampaignPickerProps {
  countryKey: string;
  campaigns: CampaignOption[];
  selectedIds: number[];
  onSelect: (id: number) => void;
  onPinGroup?: (wiserIds: number[]) => void;
  onUnpinGroup?: (wiserIds: number[]) => void;
  pinnedWiserIds?: number[];
  placeholder?: string;
  disabled?: boolean;
  /** Max height of dropdown list */
  maxHeight?: number;
}

export const SmswiserCampaignPicker: FC<SmswiserCampaignPickerProps> = ({
  countryKey: _countryKey,
  campaigns,
  selectedIds,
  onSelect,
  onPinGroup,
  onUnpinGroup,
  pinnedWiserIds = [],
  placeholder = 'Add campaign…',
  disabled = false,
  maxHeight = 280,
}) => {
  const pinnedSet = useMemo(() => new Set(pinnedWiserIds), [pinnedWiserIds]);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const available = useMemo(() => {
    const selectedSet = new Set(selectedIds);
    return campaigns.filter((c) => !selectedSet.has(c.id));
  }, [campaigns, selectedIds]);

  const filtered = useMemo(() => {
    if (!search.trim()) return available;
    const q = search.toLowerCase().trim();
    return available.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        (c.fullLabel?.toLowerCase().includes(q) ?? false),
    );
  }, [available, search]);

  const handleSelect = useCallback(
    (id: number) => {
      onSelect(id);
      setSearch('');
      setOpen(false);
    },
    [onSelect],
  );

  const isGroupPinned = useCallback(
    (wiserIds: number[]) => wiserIds.length > 0 && wiserIds.every((id) => pinnedSet.has(id)),
    [pinnedSet],
  );

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="gap-2"
          disabled={disabled || available.length === 0}
          aria-label={placeholder}
        >
          <ChevronDown className="h-4 w-4" />
          {placeholder}
        </Button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={6}
          className={cn(
            'z-[250] w-[min(480px,92vw)] max-w-[480px] rounded-lg border border-border bg-popover shadow-lg',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
          )}
          onCloseAutoFocus={(e) => e.preventDefault()}
        >
          <div className="border-b border-border p-2">
            <div className="flex items-center gap-2 rounded-md border border-input bg-background px-2 py-1.5">
              <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
              <input
                type="search"
                placeholder="Search campaigns…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                autoFocus
                autoComplete="off"
              />
            </div>
          </div>
          <div
            className="overflow-y-auto p-1"
            style={{ maxHeight }}
          >
            {filtered.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">
                {available.length === 0 ? 'All campaigns added' : 'No matches'}
              </p>
            ) : (
              <Tooltip.Provider delayDuration={300} skipDelayDuration={100}>
                <div className="space-y-0.5">
                  {filtered.map((c) => {
                    const hasGroup = c.fullLabel && c.fullLabel !== c.name;
                    const tooltipContent = hasGroup ? (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 border-b border-white/20 pb-2 text-[11px] font-semibold uppercase tracking-wider text-white/90">
                          <Layers className="h-3.5 w-3.5" />
                          {c.fullLabel!.split(', ').length} campaigns
                        </div>
                        <ul className="max-h-[200px] space-y-1.5 overflow-y-auto pr-1">
                          {c.fullLabel!.split(', ').map((campaignName, i) => (
                            <li key={i} className="text-[13px] font-medium leading-snug text-white">
                              {campaignName}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : null;
                    const wiserIds = c.wiserIds ?? [c.id];
                    const pinned = isGroupPinned(wiserIds);
                    const canPin = onPinGroup && onUnpinGroup && wiserIds.length > 0;
                    return (
                      <Tooltip.Root key={c.id}>
                        <Tooltip.Trigger asChild>
                          <div
                            className={cn(
                              'flex w-full items-center gap-2 rounded-md px-3 py-2.5 text-left text-sm transition-colors',
                              'hover:bg-accent hover:text-accent-foreground',
                              'focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-1 focus-within:outline-none',
                            )}
                          >
                            <button
                              type="button"
                              className="flex min-w-0 flex-1 flex-col text-left"
                              onClick={() => handleSelect(c.id)}
                            >
                              <span className="block break-words font-medium">{c.name}</span>
                              <span className="mt-0.5 block text-xs text-muted-foreground">ID {c.id}</span>
                            </button>
                            {canPin && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (pinned) onUnpinGroup(wiserIds);
                                  else onPinGroup(wiserIds);
                                }}
                                className={cn(
                                  'shrink-0 rounded p-1.5 transition-colors',
                                  pinned
                                    ? 'text-primary hover:bg-primary/10'
                                    : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                                )}
                                aria-label={pinned ? 'Unpin campaign group' : 'Pin campaign group'}
                              >
                                {pinned ? <Pin className="h-4 w-4 fill-current" /> : <PinOff className="h-4 w-4" />}
                              </button>
                            )}
                          </div>
                        </Tooltip.Trigger>
                        {hasGroup && (
                          <Tooltip.Portal>
                            <Tooltip.Content
                              side="right"
                              sideOffset={8}
                              avoidCollisions
                              collisionPadding={12}
                              className="!bg-zinc-900 !text-white z-[300] min-w-[220px] max-w-[360px] rounded-xl border border-zinc-600 px-4 py-3 shadow-2xl data-[state=delayed-open]:animate-in data-[state=delayed-open]:fade-in-0 data-[state=delayed-open]:zoom-in-95"
                            >
                              {tooltipContent}
                            </Tooltip.Content>
                          </Tooltip.Portal>
                        )}
                      </Tooltip.Root>
                    );
                  })}
                </div>
              </Tooltip.Provider>
            )}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};
